/**
 * O vidro do ConsultorIA — a receita da superfície, num lugar só.
 *
 * O produto é escuro por decisão do dono, com a régua de acabamento da Apple.
 * Vidro, aqui, não é enfeite: é o que permite empilhar informação densa sem
 * transformar a tela num tabuleiro de caixas opacas. Cada camada deixa passar
 * um pouco do fundo, então o olho entende profundidade sem precisar de sombra
 * pesada — e sombra pesada, em tela escura, é mancha.
 *
 * TRÊS REGRAS QUE FAZEM VIDRO PARECER VIDRO, E NÃO CINZA TRANSPARENTE:
 *
 * 1. **A borda superior é mais clara que a inferior.** Luz vem de cima. Sem o
 *    filete especular no topo, a superfície lê como um retângulo cinza com
 *    opacidade — o defeito mais comum de "glassmorphism" mal feito.
 * 2. **Saturação junto com o desfoque.** `blur` sozinho lava a cor do que está
 *    atrás; `saturate` devolve. É o que separa vidro de papel vegetal.
 * 3. **Vidro precisa de algo atrás.** Sobre preto chapado ele é invisível. Por
 *    isso o fundo do app tem campos de cor amplos e suaves (`AuroraDeFundo`):
 *    são eles que o vidro refrata.
 *
 * CUSTO: `backdrop-filter` é caro. Ele vive em POUCAS superfícies grandes — a
 * barra lateral, o cabeçalho e o cartão — nunca em item de lista, linha de
 * tabela ou chip, que se repetem dezenas de vezes por tela.
 */

/** Curvas: entrada com desaceleração longa (Apple), estado com a padrão. */
export const CURVAS = {
  entrada: 'cubic-bezier(0.22, 1, 0.36, 1)',
  estado: 'cubic-bezier(0.4, 0, 0.2, 1)',
  saida: 'cubic-bezier(0.4, 0, 1, 1)'
};

/** Durações, em ms. Acima de ~450ms o movimento vira espera. */
export const TEMPOS = {
  toque: 120,
  estado: 200,
  entrada: 340,
  numero: 750
};

/**
 * A superfície de vidro.
 *
 * @param {boolean} escuro  Modo do tema.
 * @param {'base'|'alta'|'flutuante'} nivel  Quanto a peça se destaca do fundo.
 */
export const vidro = (escuro, nivel = 'base') => {
  const niveis = {
    base: escuro ? 0.045 : 0.66,
    alta: escuro ? 0.07 : 0.78,
    flutuante: escuro ? 0.11 : 0.9
  };
  const opacidade = niveis[nivel] ?? niveis.base;
  const desfoque = nivel === 'flutuante' ? 28 : 20;

  return {
    // Os dois temas preenchem com BRANCO translúcido, e a diferença está só na
    // opacidade (4,5% no escuro, 66% no claro) — no escuro o branco a 4,5%
    // clareia o fundo quase preto; no claro ele devolve o papel. Havia um
    // ternário aqui com os dois lados idênticos, o que sugeria uma distinção
    // que não existe.
    backgroundColor: `rgba(255, 255, 255, ${opacidade})`,
    backdropFilter: `blur(${desfoque}px) saturate(1.6)`,
    WebkitBackdropFilter: `blur(${desfoque}px) saturate(1.6)`,
    border: '1px solid',
    borderColor: escuro ? 'rgba(255, 255, 255, 0.09)' : 'rgba(15, 23, 34, 0.08)',
    // A QUINA DE CIMA É DECLARADA UMA VEZ SÓ.
    //
    // Ela vinha três vezes sobre o mesmo pixel: a `border`, um `inset 0 1px 0`
    // a 13% e o `brilhoDeTopo` a 42%. Três fontes de luz para uma aresta é como
    // se chega àquele contorno leitoso que denuncia vidro de template. Quem
    // acende a quina é o `brilhoDeTopo`, que desvanece nas pontas como reflexo
    // real; aqui fica só o escurecimento da base, que é a outra metade da
    // espessura.
    boxShadow: escuro
      ? [
          'inset 0 -1px 0 rgba(0, 0, 0, 0.22)',
          '0 1px 2px rgba(0, 0, 0, 0.34)',
          '0 16px 40px -20px rgba(0, 0, 0, 0.72)'
        ].join(', ')
      : [
          'inset 0 -1px 0 rgba(15, 23, 34, 0.05)',
          '0 1px 2px rgba(15, 23, 34, 0.05)',
          '0 14px 34px -22px rgba(15, 23, 34, 0.28)'
        ].join(', ')
  };
};

/**
 * O POÇO: a superfície de DADO, escavada no cartão.
 *
 * O mundo tinha um contêiner só — o cartão de vidro — e por isso uma lista de
 * trinta e seis indicadores tinha o mesmo peso de superfície que um parágrafo
 * de três linhas. Num painel de instrumentos essas duas coisas nunca são a
 * mesma peça: o chassi é elevado, o mostrador é rebaixado. É a distinção que
 * este produto vinha prometendo na régua e não entregava na tela.
 *
 * A REGRA FÍSICA É O INVERSO EXATO DO `vidro`, e é ela que faz a peça ler como
 * escavada em vez de "cinza mais escuro":
 *
 * - `vidro` acende a quina de CIMA (luz vem de cima e bate na borda elevada) e
 *   escurece a de baixo.
 * - `poco` faz o contrário: sombra projetada para DENTRO na quina de cima — a
 *   parede do poço tapa a luz — e um fio claro na aresta de BAIXO, que é o
 *   único ponto da cavidade que a luz alcança.
 *
 * Sem desfoque: o poço vive DENTRO de um cartão que já desfoca o fundo, e um
 * segundo `backdrop-filter` aninhado custa uma camada de composição por lista,
 * em telas que têm seis delas.
 */
export const poco = (escuro) => ({
  backgroundColor: escuro ? 'rgba(0, 0, 0, 0.24)' : 'rgba(15, 23, 34, 0.035)',
  border: '1px solid',
  borderColor: escuro ? 'rgba(0, 0, 0, 0.4)' : 'rgba(15, 23, 34, 0.07)',
  borderRadius: '12px',
  boxShadow: escuro
    ? 'inset 0 2px 4px rgba(0, 0, 0, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.055)'
    : 'inset 0 2px 4px rgba(15, 23, 34, 0.06), inset 0 -1px 0 rgba(255, 255, 255, 0.9)'
});

/**
 * O filete especular do topo, como pseudo-elemento.
 *
 * Uma linha de 1px que nasce transparente nas pontas e acende no meio: é o
 * reflexo do ambiente na quina superior do vidro. É ele que dá espessura à
 * superfície — sem isso, a borda é um contorno, não um bisel.
 */
export const brilhoDeTopo = (escuro) => ({
  content: '""',
  position: 'absolute',
  top: 0,
  left: 12,
  right: 12,
  height: '1px',
  pointerEvents: 'none',
  background: escuro
    ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.42) 28%, rgba(255,255,255,0.42) 72%, transparent)'
    : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95) 30%, rgba(255,255,255,0.95) 70%, transparent)'
});

/**
 * O brilho que segue o ponteiro.
 *
 * Vidro real muda de aparência conforme o ângulo de quem olha. Aqui a variável
 * `--px/--py` é atualizada no `mousemove` do cartão e um gradiente radial de
 * baixa opacidade acompanha. Custa uma variável CSS por movimento — sem
 * re-render de React, sem animação de layout.
 *
 * Some inteiro no toque: em tela sem ponteiro, o gradiente ficaria travado no
 * último ponto tocado, o que parece defeito.
 */
export const reflexoDoPonteiro = (escuro) => ({
  content: '""',
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  opacity: 0,
  transition: `opacity ${TEMPOS.estado}ms ${CURVAS.estado}`,
  background: escuro
    ? 'radial-gradient(420px circle at var(--px, 50%) var(--py, 0%), rgba(255,255,255,0.07), transparent 45%)'
    : 'radial-gradient(420px circle at var(--px, 50%) var(--py, 0%), rgba(255,255,255,0.9), transparent 45%)',
  '@media (pointer: coarse)': { display: 'none' }
});

export default vidro;
